import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlag } from 'hooks/go-flags';
import { setPageTitle } from 'store/actions/header';
import {
  loadAudienceToStore,
  removeOneAudienceFromStore,
  removeManyAudiencesFromStore,
  updateAudienceInStore
} from 'store/actions/audience';

/** */
import { Button } from 'app/shared/button';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDatatable from 'app/shared/datatable/web/datatable';
import GmModal from 'app/shared/modal/modal';
import ListCreationForm from 'app/member/modules/mailing-lists/create/create';
import CloneMailingItem from '../clone/clone';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { toReadableNumber } from 'app/shared/utils/number';
import { truncateText } from 'app/shared/utils/general';
import { PermissionsContext } from 'contexts/permissions';
import { useContactService } from 'hooks/users/contact';
import { useAudienceService } from 'hooks/users/audience';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { filter_config } from './config/filter';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import { Spacer } from 'app/layouts/generic';
import useScreenSize from 'hooks/size';

const audience_type_tabs = [
  { value: 'user', label: 'User' },
  { value: 'system', label: 'System' }
];

const ListMailingLists = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const { isFeatureAllowed } = useFeatureFlag();
  const { isMobile } = useScreenSize();
  const { tenant_id } = useSelector((state) => state.user_data);
  const audiences_in_store = useSelector((state) => state.audiences);
  const { permissions } = useContext(PermissionsContext);

  const {
    deleteAudienceById,
    deleteAudiences,
    fetchAudiences,
    searchAudiences,
    updateAudience,
    updateAudiences
  } = useAudienceService();
  const { deleteAudienceContacts, fetchAudienceContacts } = useContactService();

  const [audience_to_modify, setAudienceToModify] = useState({});
  const [audiences_to_modify, setAudiencesToModify] = useState([]);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mailing_lists, setMailingLists] = useState([]);
  const [selected_item, setSelectedItem] = useState({});
  const [show_create_audience_modal, setShowCreateAudienceModal] = useState(false);
  const [show_clone_audience_modal, setShowCloneAudienceModal] = useState(false);
  const [show_archival_options, setShowArchivalOptions] = useState(false);
  const [show_bulk_deletion_confirmation, setShowBulkDeletionConfirmation] = useState(false);
  const [show_clone_audience_options, setShowCloneAudienceOptions] = useState(false);
  const [show_contact_deletion_confirmation, setShowContactDeletionConfirmation] = useState(false);
  const [show_deletion_confirmation, setShowDeletionConfirmation] = useState(false);
  const [test_audience_id, setTestAudienceId] = useState();
  const [total_audiences, setTotalAudiences] = useState(0);
  const [audience_type, setAudienceType] = useState('user');

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Audiences', path: '.' }]));

    isFeatureAllowed('Feature.AudienceArchival', { tenant_id }).then(({ is_permitted }) => {
      setShowArchivalOptions(is_permitted);
    });
    isFeatureAllowed('Feature.AudienceCloning', { tenant_id }).then(({ is_permitted }) => {
      setShowCloneAudienceOptions(is_permitted);
    });
  }, []);

  useEffect(() => {
    const lists = Object.values(audiences_in_store);
    const system_audiences = [];
    const other_audiences = [];
    lists.forEach((list) => {
      if (list.type === 'system') system_audiences.push(list);
      else other_audiences.push(list);
    });

    const test_audience_id = lists.find((item) => item.is_test_audience)?.id;
    setTestAudienceId(test_audience_id);
    setMailingLists([...system_audiences, ...other_audiences]);
  }, [audiences_in_store]);

  const config = {
    actions: {
      single: (audience) => [
        [
          { label: 'View', value: 'view' },
          {
            label: 'Clone',
            value: 'clone',
            hidden: !show_clone_audience_options || audience.type === 'system'
          },
          {
            label: audience.is_archived ? 'Unarchive' : 'Archive',
            value: audience.is_archived ? 'unarchive' : 'archive',
            hidden: !show_archival_options || audience.type === 'system'
          }
        ],
        [
          {
            color: 'red',
            label: 'Delete',
            value: 'delete',
            hidden: audience.type === 'system'
          },
          {
            color: 'red',
            label: 'Delete contacts',
            value: 'delete_contacts',
            hidden: audience.type === 'system'
          }
        ]
      ],
      bulk: [
        [
          {
            label: 'Archive',
            value: 'archive'
          },
          {
            label: 'Unarchive',
            value: 'unarchive'
          }
        ],
        [
          {
            color: 'red',
            label: 'Delete',
            value: 'delete'
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Name',
        key: 'name',
        sortable: true,
        isTitle: true
      },
      {
        title: 'Description',
        key: 'description',
        formatter: (value) => truncateText(value, 35) || 'N/A',
        isTagline: true
      },
      {
        title: 'No. of Contacts',
        key: 'contacts',
        formatter: (value) => toReadableNumber(value) || '0'
      },
      {
        title: 'Date created',
        key: 'created_on',
        sortable: true,
        formatter: (value) => (value ? new Date(value).toDateString() : ''),
        isMetadata: true
      }
    ],
    is_search_mode,
    items: mailing_lists,
    num_of_rows: 10,
    search_key: 'name',
    search_text: ''
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/audiences/${id}/query`);
  };

  const handleBulkArchivalToggle = async (action, data = []) => {
    const user_audiences = data.filter((audience) => audience.type !== 'system');
    const ids_to_update = user_audiences.map((audience) => audience.id);
    setLoading(true);
    const result = await updateAudiences({
      data: {
        options: { id: ids_to_update.join(',') },
        data: {
          is_archived: action === 'archive'
        }
      }
    });
    setLoading(false);

    if (!result) return;
    handleDataRequest();
    toast.success(
      `Selected Audiences have been ${action === 'archive' ? 'archived' : 'unarchived'}`
    );
  };

  const handleDataRequest = async (
    page = 0,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    try {
      setLoading(true);
      const filter_query =
        !options.filter_query || options.filter_query === '&'
          ? 'bool=-is_archived'
          : options.filter_query;
      const query = `type=${audience_type}&${filter_query}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const return_query = `return_only=name,id,description,created_on,is_archived,is_test_audience,time_stamp,type`;
      const query_string = `${query}&${sort_query}&${return_query}&page=${page}&population=${population}`;

      const { audiences, size } = await fetchAudiences({ query_string });
      const parsed_audiences = audiences.map((a) => ({
        ...a,
        contacts: 0
      }));

      dispatch(loadAudienceToStore(parsed_audiences));
      setTotalAudiences(size);

      for (let i = 0; i < audiences.length; i++) {
        const { id: audience_id } = audiences[i];
        fetchAudienceContacts(audience_id, { query_string: 'count=1' }).then(
          ({ size: contacts }) => {
            dispatch(updateAudienceInStore({ id: audience_id, contacts }));
          }
        );
      }
    } catch (e) {
      dispatch(loadAudienceToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleDatatableAction = async (payload) => {
    const { name, type, data } = payload;
    if (type === 'single') {
      switch (name) {
        case 'view':
          history(`/audiences/${data.id}/query`);
          break;
        case 'clone':
          setShowCloneAudienceModal(true);
          setSelectedItem(data);
          break;
        case 'archive':
        case 'unarchive':
          handleSingleArchivalToggle(name, data);
          break;
        case 'delete':
          setAudienceToModify(data);
          setShowDeletionConfirmation(true);
          break;
        case 'delete_contacts':
          setAudienceToModify(data);
          setShowContactDeletionConfirmation(true);
          break;
        default:
      }
    } else if (type === 'bulk') {
      switch (name) {
        case 'archive':
        case 'unarchive':
          handleBulkArchivalToggle(name, data);
          break;
        case 'delete':
          setAudiencesToModify(data);
          setShowBulkDeletionConfirmation(true);
          break;
        default:
      }
    }
  };

  const handleBulkDeletion = async (is_permitted) => {
    if (is_permitted) {
      const user_audiences = audiences_to_modify.filter((audience) => audience.type !== 'system');
      const audience_ids = user_audiences.map((audience) => audience.id);
      const data = { options: { id: audience_ids.join(',') } };
      const result = await deleteAudiences({ data });

      if (result) {
        dispatch(removeManyAudiencesFromStore(audience_ids));
      }
    }

    setAudiencesToModify([]);
    setShowBulkDeletionConfirmation(false);
  };

  const handleContactDeletion = async (is_permitted) => {
    if (is_permitted) {
      toast.info('Deleting contacts from system.');
      const result = await deleteAudienceContacts(audience_to_modify.id);
      if (result) {
        window.location.reload();
      }
    }

    setAudienceToModify({});
    setShowContactDeletionConfirmation(false);
  };

  const handleDeletion = async (is_permitted) => {
    if (is_permitted) {
      toast.info('Deleting audience.');
      const result = await deleteAudienceById(audience_to_modify?.id);
      if (result) {
        dispatch(removeOneAudienceFromStore(audience_to_modify.id));
      }
    }

    setAudienceToModify({});
    setShowDeletionConfirmation(false);
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const filter_query =
        !options.filter_query || options.filter_query === '&'
          ? 'bool=-is_archived'
          : options.filter_query;
      const query = `type=${audience_type}&${filter_query}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const return_query = `return_only=name,id,description,created_on,is_archived,is_test_audience,time_stamp,type`;
      const query_string = `${query}&${sort_query}&${return_query}&page=${page}&population=${population}`;
      const { audiences, size } = await searchAudiences(keys, keyword, { query_string });

      const aud_ids = [];
      const responses = await Promise.all(
        audiences.map((audience) => {
          aud_ids.push(audience.id);
          return fetchAudienceContacts(audience.id, { query_string: `count=1` });
        })
      );

      const count_by_id = responses.reduce((s, { size }, index) => {
        return {
          ...s,
          [aud_ids[index]]: size
        };
      }, {});

      const parsed_audiences = audiences.map((a) => ({ ...a, contacts: count_by_id[a.id] }));
      dispatch(loadAudienceToStore(parsed_audiences));
      setTotalAudiences(size);
    } catch (e) {
      dispatch(loadAudienceToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSingleArchivalToggle = async (action = 'archive', data = {}) => {
    const data_to_update = {
      is_archived: action === 'archive'
    };
    setLoading(true);
    const result = await updateAudience(data.id, {
      data: data_to_update
    });
    setLoading(false);

    if (!result) return;
    handleDataRequest();
    toast.success(`Audience ${action === 'archive' ? 'archived' : 'unarchived'}`);
  };

  const handleAudienceTypeChange = (type) => {
    dispatch(loadAudienceToStore([]));
    setAudienceType(type);
  };

  const audience_type_toggle = (
    <SegmentedTabs
      options={audience_type_tabs}
      value={audience_type}
      onChange={handleAudienceTypeChange}
      label="Audience type"
    />
  );

  const table_actions = (
    <>
      <Button
        text="Create audience"
        icon_name="add"
        onClick={() => setShowCreateAudienceModal(true)}
        disabled={!permissions['audience:create']}
      />
    </>
  );

  return (
    <PageContentWrapper>
      {isMobile ? (
        <>
          {audience_type_toggle}
          <Spacer multiple={2} />
          <MobileDatatable
            key={audience_type}
            config={config}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
            showHeader
          />
        </>
      ) : (
        <div>
          <WebDatatable
            action={handleDatatableAction}
            checkbox
            config={{
              ...config,
              total_count: total_audiences
            }}
            onClick={handleItemClick}
            has_filter
            filter_config={filter_config()}
            header_tabs={audience_type_toggle}
            reset_key={audience_type}
            loading_data={loading}
            table_actions={table_actions}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
          {show_create_audience_modal && (
            <GmModal
              title="Create Audience"
              show_title={true}
              show_modal={show_create_audience_modal}
              onClose={() => setShowCreateAudienceModal(false)}
            >
              <ListCreationForm current_test_audience_id={test_audience_id} />
            </GmModal>
          )}
          <GmModal
            title="Clone Audience"
            show_title={true}
            show_modal={show_clone_audience_modal}
            onClose={() => setShowCloneAudienceModal(false)}
          >
            <CloneMailingItem line_item={selected_item} />
          </GmModal>
          <ConfirmationDialog
            type="error"
            icon_name="trash"
            action_text="Yes, delete"
            message={
              'You are about to delete an audience. Once completed, this action cannot be undone.'
            }
            title="Delete audience?"
            is_open={show_deletion_confirmation}
            callback={handleDeletion}
          />
          <ConfirmationDialog
            type="error"
            icon_name="trash"
            action_text="Yes, delete"
            title="Delete audiences?"
            message={
              'You are about to delete multiple audiences. Once completed, this action cannot be undone.'
            }
            is_open={show_bulk_deletion_confirmation}
            callback={handleBulkDeletion}
          />
          <ConfirmationDialog
            type="error"
            icon_name="trash"
            action_text="Yes, delete"
            title="Delete contacts from system?"
            message={
              'You are about to delete all contacts in this audience from the system. Once completed, this action cannot be undone.'
            }
            is_open={show_contact_deletion_confirmation}
            callback={handleContactDeletion}
          />
        </div>
      )}
    </PageContentWrapper>
  );
};

export default ListMailingLists;
